"use client"

import { useEffect, useRef, useState } from "react"
import type { RefObject } from "react"

import { getHeroMotion, HERO_CYCLE_MS, sameHeroMotion } from "./hero-motion"
import type { HeroMotion } from "./hero-motion"

const STILL_MOTION = getHeroMotion(0)

/** Preserve the device sequence while paused, offscreen, or in a hidden tab. */
export function useHeroMotion(userPaused = false): {
  artwork: RefObject<HTMLDivElement | null>
  motion: HeroMotion
  paused: boolean
  reducedMotion: boolean
} {
  const [reducedMotion, setReducedMotion] = useState(true)
  const [inView, setInView] = useState(false)
  const [pageVisible, setPageVisible] = useState(false)
  const [motion, setMotion] = useState(STILL_MOTION)
  const elapsed = useRef(0)
  const artwork = useRef<HTMLDivElement>(null)
  const paused = userPaused || reducedMotion || !inView || !pageVisible

  useEffect(() => {
    const element = artwork.current
    if (!element) {
      return undefined
    }

    const preference = window.matchMedia("(prefers-reduced-motion: reduce)")
    const updatePreference = () => setReducedMotion(preference.matches)
    const updateVisibility = () => setPageVisible(document.visibilityState === "visible")
    const observer = new IntersectionObserver(([entry]) => {
      setInView(Boolean(entry?.isIntersecting))
    })

    updatePreference()
    updateVisibility()
    observer.observe(element)
    preference.addEventListener("change", updatePreference)
    document.addEventListener("visibilitychange", updateVisibility)

    return () => {
      observer.disconnect()
      preference.removeEventListener("change", updatePreference)
      document.removeEventListener("visibilitychange", updateVisibility)
    }
  }, [])

  useEffect(() => {
    if (paused) {
      return undefined
    }

    let previous = performance.now()
    const timer = window.setInterval(() => {
      const now = performance.now()
      elapsed.current = (elapsed.current + now - previous) % HERO_CYCLE_MS
      previous = now
      const next = getHeroMotion(elapsed.current)
      setMotion((current) => sameHeroMotion(current, next) ? current : next)
    }, 60)
    return () => window.clearInterval(timer)
  }, [paused])

  return {
    artwork, motion, paused, reducedMotion,
  }
}
